import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  User,
  FileText,
  Paperclip,
  Upload,
  Calendar,
  MessageSquare,
  Activity,
  Edit,
  CheckCircle,
  Send,
  Flag,
  ArrowRight,
  Eye,
  Image,
  Download,
  Trash2,
} from "lucide-react";
import { Task, StoryComment, WorkLog } from "../../types/types";
import { realAPI } from "../../services/realAPI";
import { useNotifications } from "../NotificationSystem/NotificationSystem";

// Define interface for organization user
interface OrganizationUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  role: string;
  createdAt: string | null;
  emailVerified: boolean;
  teamIds: string[];
  organizationId: string | null;
}

// Component for handling authenticated images with fallback
const AuthenticatedImage: React.FC<{
  thumbnailPath: string;
  alt: string;
  className: string;
  fallbackIcon: React.ReactNode;
}> = ({ thumbnailPath, alt, className, fallbackIcon }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let currentUrl: string | null = null;

    const loadImage = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        const blobUrl = await realAPI.fetchAuthenticatedFile(thumbnailPath);

        if (isMounted) {
          currentUrl = blobUrl;
          setImageSrc(blobUrl);
        }
      } catch (error) {
        console.error("❌ Failed to load authenticated image:", error);
        if (isMounted) {
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [thumbnailPath]);

  if (isLoading) {
    return (
      <div
        className={`${className} bg-gray-100 animate-pulse flex items-center justify-center`}
      >
        <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (hasError || !imageSrc) {
    return <>{fallbackIcon}</>;
  }

  return <img src={imageSrc} alt={alt} className={className} />;
};

interface Sprint {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: "planning" | "active" | "completed";
}

interface StoryModalProps {
  task: Task;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (updatedTask: Task) => void;
  sprints?: Sprint[];
  embedded?: boolean; // render inline instead of full-screen modal
}

const StoryModal: React.FC<StoryModalProps> = ({
  task,
  isOpen,
  onClose,
  onUpdate,
  sprints = [],
  embedded = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(task);
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState<StoryComment[]>([]);
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isLoadingWorkLogs, setIsLoadingWorkLogs] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState<"comments" | "activity">(
    "comments"
  );
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState<
    OrganizationUser[]
  >([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { success, error } = useNotifications();

  // Attachment popup state
  const [showAttachmentPopup, setShowAttachmentPopup] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState<{
    filename: string;
    url: string;
    thumbnailUrl?: string;
  } | null>(null);

  // Track which attachment is being deleted
  const [deletingAttachment, setDeletingAttachment] = useState<string | null>(
    null
  );

  // Debug: Track task prop changes
  useEffect(() => {
    console.log("🎯 StoryModal task prop changed:", {
      taskId: task?.id,
      attachments: task?.attachments,
      attachmentUrls: task?.attachmentUrls,
      editedTaskAttachments: editedTask?.attachments,
      editedTaskAttachmentUrls: editedTask?.attachmentUrls,
    });
  }, [task, editedTask]);

  // Load organization data from localStorage and current user
  useEffect(() => {
    console.log("🔍 Loading user data from localStorage...");
    try {
      const organizationDataString = localStorage.getItem("organizationData");
      console.log(
        "📂 organizationDataString:",
        organizationDataString ? "Found" : "Not found"
      );
      if (organizationDataString) {
        const organizationData = JSON.parse(organizationDataString);
        console.log("📊 organizationData:", organizationData);
        if (organizationData && Array.isArray(organizationData.users)) {
          setOrganizationUsers(organizationData.users);
          console.log(
            "👥 Set organization users:",
            organizationData.users.length,
            "users"
          );
          console.log("👤 First user for fallback:", organizationData.users[0]);
        } else {
          console.log("⚠️ organizationData.users is not an array or is empty");
        }
      }

      // Get current user from localStorage
      const currentUserString = localStorage.getItem("currentUser");
      console.log("👤 currentUserString:", currentUserString);

      if (currentUserString) {
        const user = JSON.parse(currentUserString);
        console.log("✅ Parsed current user:", user);
        setCurrentUser(user);
      } else {
        console.log("❌ No current user found in localStorage");

        // Try alternative storage keys that might be used during development/testing
        const userSession = localStorage.getItem("userSession");
        const authUser = localStorage.getItem("authUser");
        const user = localStorage.getItem("user");

        console.log("🔍 Checking alternative keys:");
        console.log("  - userSession:", userSession ? "Found" : "Not found");
        console.log("  - authUser:", authUser ? "Found" : "Not found");
        console.log("  - user:", user ? "Found" : "Not found");

        // Check all localStorage keys for debugging
        console.log("📋 All localStorage keys:", Object.keys(localStorage));

        // Try userSession as fallback
        if (userSession) {
          try {
            const sessionUser = JSON.parse(userSession);
            if (sessionUser && sessionUser.id) {
              console.log("✅ Found user data in userSession:", sessionUser);
              setCurrentUser(sessionUser);
            }
          } catch (e) {
            console.log("❌ userSession is not valid JSON");
          }
        }
      }
    } catch (err) {
      console.error("❌ Error loading organization data:", err);
    }
  }, []);

  // Load comments when modal opens
  const loadComments = useCallback(async () => {
    try {
      setIsLoadingComments(true);
      const commentsData = await realAPI.storyComments.getComments(task.id);
      setComments(commentsData || []);
    } catch (err) {
      console.error("Error loading comments:", err);
      error(
        "Failed to Load Comments",
        "Unable to load comments for this story."
      );
    } finally {
      setIsLoadingComments(false);
    }
  }, [error, task.id]);

  const loadWorkLogs = useCallback(async () => {
    try {
      setIsLoadingWorkLogs(true);
      const workLogsData = await realAPI.workLogs.getWorkLogs(task.id);
      setWorkLogs(workLogsData || []);
    } catch (err) {
      console.error("Error loading work logs:", err);
      error(
        "Failed to Load Activity",
        "Unable to load activity logs for this story."
      );
    } finally {
      setIsLoadingWorkLogs(false);
    }
  }, [error, task.id]);

  useEffect(() => {
    if (isOpen && task.id) {
      loadComments();
      loadWorkLogs();
    }
  }, [isOpen, loadComments, loadWorkLogs, task.id]);

  // Sync editedTask with task prop when task changes
  useEffect(() => {
    console.log("🔄 Task prop changed, syncing editedTask:", task);
    setEditedTask(task);
  }, [task]);

  const closeAttachmentPopup = useCallback(() => {
    setShowAttachmentPopup(false);
    setCurrentAttachment(null);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".move-dropdown")) {
        setShowMoveDropdown(false);
      }
    };

    if (showMoveDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMoveDropdown]);

  // Handle escape key for attachment popup
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showAttachmentPopup) {
        closeAttachmentPopup();
      }
    };

    if (showAttachmentPopup) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeAttachmentPopup, showAttachmentPopup]);

  // In embedded mode, always render; otherwise respect isOpen
  if (!embedded && !isOpen) return null;

  console.log("🔄 StoryModal isOpen:", isOpen, "task:", task);

  const priorityColors = {
    low: "bg-green-50 text-green-700 border-green-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    high: "bg-red-50 text-red-700 border-red-200",
  };

  const statusColors = {
    todo: "bg-gray-50 text-gray-700 border-gray-200",
    inProgress: "bg-blue-50 text-blue-700 border-blue-200",
    deployed: "bg-purple-50 text-purple-700 border-purple-200",
    done: "bg-green-50 text-green-700 border-green-200",
  };

  const columnLabels = {
    todo: "To Do",
    inProgress: "In Progress",
    deployed: "Deployed",
    done: "Done",
  };

  const getTypeIcon = (type?: string) => {
    switch (type || task.type || "story") {
      case "story":
        return "📚";
      case "bug":
        return "🐛";
      case "task":
        return "✅";
      default:
        return "📚";
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "high":
        return "🔴";
      case "medium":
        return "🟡";
      case "low":
        return "🟢";
      default:
        return "⚪";
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updatedTask = {
        ...editedTask,
        assigneeUserId: editedTask.assigneeUserId
          ? editedTask.assigneeUserId.toString()
          : null,
      };

      await realAPI.updateTask(updatedTask.id, {
        title: updatedTask.title,
        description: updatedTask.description,
        priority: updatedTask.priority,
        storyPoints: updatedTask.storyPoints,
        type: updatedTask.type,
        assigneeUserId: updatedTask.assigneeUserId,
      });

      // If taskColumn (status) changed, update it separately
      if (task.taskColumn !== editedTask.taskColumn) {
        await realAPI.updateTaskColumn(
          String(updatedTask.id),
          editedTask.taskColumn || "todo"
        );
      }

      // Reload work logs to show the new activity
      await loadWorkLogs();

      if (onUpdate) {
        onUpdate(updatedTask);
      }

      // Reload work logs to show the latest activity
      loadWorkLogs();

      success("Story Updated", "Your changes have been saved successfully.");
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating task:", err);
      error("Update Failed", "Failed to save changes. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setEditedTask(task);
    setIsEditing(false);
  };

  const handleAddComment = async () => {
    console.log("🔍 handleAddComment called");

    if (!newComment.trim()) {
      return;
    }

    if (!currentUser) {
      error("Authentication Required", "Please log in to add comments.");
      return;
    }

    try {
      setIsAddingComment(true);

      const addedComment = await realAPI.storyComments.addComment(
        task.id,
        currentUser.id,
        newComment.trim()
      );

      setComments([...comments, addedComment]);
      setNewComment("");
      success("Comment Added", "Your comment has been posted.");
    } catch (err) {
      console.error("❌ Error adding comment:", err);
      error(
        "Failed to Add Comment",
        "Unable to add your comment. Please try again."
      );
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Check if task exists and has a real (saved) ID, not a temporary one
    if (
      !task?.id ||
      (typeof task.id === "string" && task.id.startsWith("item-"))
    ) {
      error(
        "Save Story First",
        "Please save the story before adding attachments."
      );
      event.target.value = "";
      return;
    }

    // Validate each file
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "text/plain",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const validFiles: File[] = [];

    for (const file of fileArray) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        error(
          "File Too Large",
          `${file.name} is larger than 10MB. Please select a smaller file.`
        );
        continue;
      }

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        error(
          "Invalid File Type",
          `${file.name} is not a supported file type. Please select an image, PDF, or document file.`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      event.target.value = "";
      return;
    }

    try {
      setIsUploadingFile(true);

      console.log("🚀 Starting multiple file upload for task:", task?.id);
      console.log(
        "📁 Files to upload:",
        validFiles.map((f) => ({ name: f.name, size: f.size }))
      );

      const uploadResponse = await realAPI.uploadTaskAttachments(
        task.id,
        validFiles
      );
      console.log("📎 Multiple upload response received:", uploadResponse);

      // Refresh the task data to show the new attachments
      console.log("🔄 Refreshing task data after multiple upload...");
      try {
        const updatedTask = await realAPI.getTaskById(task.id);
        console.log(
          "🎯 Updated task received after multiple upload:",
          updatedTask
        );
        console.log("📎 Updated task attachments:", updatedTask.attachments);

        // Update both the edited task and notify the parent component
        console.log("💾 Updating local state and parent component...");
        console.log(" About to call setEditedTask with:", updatedTask);
        setEditedTask(updatedTask);
        console.log("📞 Calling onUpdate with updated task...");
        if (onUpdate) {
          onUpdate(updatedTask);
        }
        console.log("✅ All update calls completed");
      } catch (refreshError) {
        console.error("❌ Error refreshing task data:", refreshError);
        error(
          "Upload Complete",
          "Files uploaded but failed to refresh. Please close and reopen the task to see attachments."
        );
      }

      // Reload work logs to show the upload activity
      await loadWorkLogs();

      const fileCount = validFiles.length;
      const fileNames =
        validFiles.length === 1
          ? validFiles[0].name
          : `${validFiles.length} files`;

      success(
        "Files Uploaded",
        `${fileNames} ${
          fileCount === 1 ? "has" : "have"
        } been attached to this story.`
      );

      // Clear the input
      event.target.value = "";
    } catch (err: any) {
      console.error("❌ Error uploading files:", err);

      // Handle specific error types
      if (err.details?.isMaxSizeExceeded) {
        error(
          "File Too Large",
          "One or more files exceed the maximum allowed limit. Please choose smaller files or contact your administrator."
        );
      } else if (err.details?.status === 413) {
        error(
          "Files Too Large",
          "The files are too large for the server to process. Please choose smaller files."
        );
      } else if (err.details?.message) {
        error("Upload Failed", err.details.message);
      } else {
        error("Upload Failed", "Unable to upload the files. Please try again.");
      }
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleViewAttachment = (
    filename: string,
    url: string,
    thumbnailUrl?: string
  ) => {
    setCurrentAttachment({ filename, url, thumbnailUrl });
    setShowAttachmentPopup(true);
  };

  const handleRemoveAttachment = async (
    filename: string,
    url: string
  ): Promise<boolean> => {
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to remove "${filename}"?`)) {
      return false;
    }

    try {
      console.log("🗑️ Starting attachment removal:", { filename, url });
      setDeletingAttachment(url); // Set loading state

      await realAPI.deleteTaskAttachment(task.id, url);
      console.log("✅ Attachment deleted successfully");

      // Refresh the task data to show updated attachments
      console.log("🔄 Refreshing task data after deletion...");
      try {
        const updatedTask = await realAPI.getTaskById(task.id);
        console.log("🎯 Updated task received after deletion:", updatedTask);
        console.log("📎 Updated task attachments:", updatedTask.attachments);

        // Update both the edited task and notify the parent component
        console.log(
          "💾 Updating local state and parent component after deletion..."
        );
        setEditedTask(updatedTask);
        if (onUpdate) {
          onUpdate(updatedTask);
        }
        console.log("✅ All update calls completed after deletion");
      } catch (refreshError) {
        console.error(
          "❌ Error refreshing task data after deletion:",
          refreshError
        );
        error(
          "Deletion Complete",
          "Attachment removed but failed to refresh. Please close and reopen the task."
        );
      }

      // Reload work logs to show the removal activity
      await loadWorkLogs();

      success(
        "Attachment Removed",
        `"${filename}" has been removed from this story.`
      );
      return true;
    } catch (err: any) {
      console.error("❌ Error removing attachment:", err);

      if (err.message) {
        error("Removal Failed", err.message);
      } else {
        error(
          "Removal Failed",
          "Unable to remove the attachment. Please try again."
        );
      }
      return false;
    } finally {
      setDeletingAttachment(null); // Clear loading state
    }
  };

  // Delete from within the attachment popup and close it on success
  const handleDeleteCurrentAttachment = async () => {
    if (!currentAttachment) return;
    const ok = await handleRemoveAttachment(
      currentAttachment.filename,
      currentAttachment.url
    );
    if (ok) {
      closeAttachmentPopup();
    }
  };

  // Programmatic download that respects auth and backend-provided filename
  const downloadFile = async (rawFilenameOrUrl: string) => {
    try {
      const token = localStorage.getItem("accessToken");
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:9550";

      // Accept either a full URL or just the filename
      const rawTail = (
        rawFilenameOrUrl?.split("/").pop() ||
        rawFilenameOrUrl ||
        "file"
      ).toString();
      // Strip query/hash fragments from the last segment to avoid breaking the backend path param
      const rawName = rawTail.split("?")[0].split("#")[0];
      const requestUrl = `${baseUrl}/api/v1/attachments/view/${encodeURIComponent(
        rawName
      )}?dl=true`;

      const response = await fetch(requestUrl, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }

      // Content-Disposition-derived filename (fallback to raw)
      const cd = response.headers.get("Content-Disposition") || "";
      let suggestedName = rawName;
      try {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
          cd
        );
        if (match) {
          const raw = match[1] || match[2];
          suggestedName = decodeURIComponent(raw);
        }
      } catch {
        // ignore
      }

      const originalExt = (suggestedName.split(".").pop() || "").toLowerCase();
      const urlExt = (rawName.split(".").pop() || "").toLowerCase();

      const extToMime: Record<string, string> = {
        pdf: "application/pdf",
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        gif: "image/gif",
        webp: "image/webp",
        svg: "image/svg+xml",
        bmp: "image/bmp",
        txt: "text/plain",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      };

      // Determine the most likely extension and mime
      const chosenExt = originalExt || urlExt || "";
      const desiredMime = extToMime[chosenExt] || "application/octet-stream";

      let blob = await response.blob();

      // If server sent the wrong type (e.g., application/json), override it based on extension
      if (!blob.type || blob.type === "application/json") {
        blob = new Blob([blob], { type: desiredMime });
      }

      // Fix Safari iOS quirk: sometimes appends .json when saving
      // If we know the extension is not json but the name ends with .json, replace it
      if (chosenExt && chosenExt !== "json" && /\.json$/i.test(suggestedName)) {
        suggestedName = suggestedName.replace(/\.json$/i, `.${chosenExt}`);
      }

      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.userAgent.includes("Mac") && "ontouchend" in document);

      if (isSafari && isIOS) {
        // iOS Safari: open a blank tab synchronously, then navigate it once data URL is ready
        const popup = window.open("about:blank", "_blank");
        if (!popup) {
          // Popup blocked; fall back to showing an inline open via same tab
          const readerBlocked = new FileReader();
          readerBlocked.onloadend = () => {
            try {
              const dataUrl = readerBlocked.result as string;
              window.location.href = dataUrl;
            } catch (e) {
              console.error(
                "Failed to open data URL on iOS Safari (fallback)",
                e
              );
              error(
                "Download Failed",
                "Unable to open the file on iOS Safari."
              );
            }
          };
          readerBlocked.readAsDataURL(blob);
          return;
        }

        // Keep user feedback in the new tab while loading
        try {
          popup.document.write(
            "<p style='font-family:system-ui; padding:1rem'>Preparing download…</p>"
          );
        } catch {}

        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            const dataUrl = reader.result as string;
            popup.location.href = dataUrl;
          } catch (e) {
            console.error("Failed to navigate iOS Safari popup to data URL", e);
            try {
              popup.close();
            } catch {}
            error("Download Failed", "Unable to open the file on iOS Safari.");
          }
        };
        reader.readAsDataURL(blob);
        return;
      }

      // Other browsers: use a temporary anchor with download attribute
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = suggestedName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("❌ Download error:", err);
      error(
        "Download Failed",
        err?.message || "Unable to download the file. Please try again."
      );
    }
  };

  const getFileIcon = (url: string) => {
    const extension = url.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "webp":
        return <Image className="w-8 h-8" />;
      case "pdf":
        return <FileText className="w-8 h-8 text-red-600" />;
      case "doc":
      case "docx":
        return <FileText className="w-8 h-8 text-blue-600" />;
      default:
        return <Paperclip className="w-8 h-8" />;
    }
  };

  const getFileName = (url: string) => {
    return url.split("/").pop() || "Unknown file";
  };

  const content = (
    <>
      <div
        className={`bg-white rounded-xl ${
          embedded ? "shadow" : "shadow-2xl"
        } w-full ${embedded ? "max-w-none" : "max-w-4xl"} ${
          embedded ? "max-h-none" : "max-h-[95vh]"
        } ${embedded ? "my-0" : "my-4"} flex flex-col border border-gray-200`}
      >
        {/* Modern Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl shadow-lg flex-shrink-0">
              {getTypeIcon()}
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input
                  type="text"
                  value={editedTask.title || ""}
                  onChange={(e) =>
                    setEditedTask({ ...editedTask, title: e.target.value })
                  }
                  className="text-xl lg:text-2xl xl:text-3xl font-semibold text-gray-900 bg-white border-2 border-blue-300 focus:border-blue-500 outline-none w-full py-3 px-4 rounded-lg shadow-sm min-w-0"
                  placeholder="Enter story title..."
                />
              ) : (
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-semibold text-gray-900 leading-tight pr-4">
                  {task?.title || "Untitled Story"}
                </h1>
              )}
              <div className="flex items-center space-x-3 mt-1">
                <span className="text-sm text-gray-500">Story #{task?.id}</span>
                <div className="flex items-center space-x-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      statusColors[
                        task?.taskColumn as keyof typeof statusColors
                      ] || "bg-gray-50 text-gray-700 border-gray-200"
                    }`}
                  >
                    {task?.taskColumn
                      ? columnLabels[
                          task.taskColumn as keyof typeof columnLabels
                        ]
                      : "Unassigned"}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      priorityColors[
                        task?.priority as keyof typeof priorityColors
                      ] || "bg-gray-50 text-gray-700 border-gray-200"
                    }`}
                  >
                    {getPriorityIcon(task?.priority || "medium")}{" "}
                    {(task?.priority || "medium").charAt(0).toUpperCase() +
                      (task?.priority || "medium").slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg p-2 transition-all duration-200 flex-shrink-0 ml-4"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modern Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-8">
            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Assignee Card */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-900">Assignee</h3>
                  </div>
                  {isEditing ? (
                    <select
                      value={editedTask.assigneeUserId || ""}
                      onChange={(e) =>
                        setEditedTask({
                          ...editedTask,
                          assigneeUserId: e.target.value || undefined,
                        })
                      }
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {organizationUsers.map((user) => (
                        <option key={user.id} value={user.id.toString()}>
                          {user.firstName} {user.lastName} ({user.jobTitle})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center space-x-3">
                      {task?.assigneeUserId ? (
                        <>
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center rounded-full text-sm font-semibold shadow-md">
                            {(() => {
                              const user = organizationUsers.find(
                                (u) =>
                                  u.id.toString() ===
                                  task.assigneeUserId?.toString()
                              );
                              return user
                                ? `${user.firstName[0]}${user.lastName[0]}`
                                : "?";
                            })()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {(() => {
                                const user = organizationUsers.find(
                                  (u) =>
                                    u.id.toString() ===
                                    task.assigneeUserId?.toString()
                                );
                                return user
                                  ? `${user.firstName} ${user.lastName}`
                                  : "Unknown User";
                              })()}
                            </p>
                            <p className="text-sm text-gray-500">
                              {(() => {
                                const user = organizationUsers.find(
                                  (u) =>
                                    u.id.toString() ===
                                    task.assigneeUserId?.toString()
                                );
                                return user?.jobTitle || "Team Member";
                              })()}
                            </p>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center space-x-3 text-gray-500">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5" />
                          </div>
                          <span className="text-gray-500">Unassigned</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Story Points & Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg">🎯</span>
                      <h3 className="font-medium text-gray-900">
                        Story Points
                      </h3>
                    </div>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editedTask.storyPoints || ""}
                        onChange={(e) =>
                          setEditedTask({
                            ...editedTask,
                            storyPoints: e.target.value
                              ? parseInt(e.target.value)
                              : undefined,
                          })
                        }
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Points"
                        min="1"
                        max="100"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-purple-600">
                        {task?.storyPoints || "—"}
                      </span>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg">{getTypeIcon()}</span>
                      <h3 className="font-medium text-gray-900">Type</h3>
                    </div>
                    {isEditing ? (
                      <select
                        value={editedTask.type || "story"}
                        onChange={(e) =>
                          setEditedTask({
                            ...editedTask,
                            type: e.target.value as "story" | "bug" | "task",
                          })
                        }
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="story">📚 Story</option>
                        <option value="bug">🐛 Bug</option>
                        <option value="task">✅ Task</option>
                      </select>
                    ) : (
                      <span className="text-sm font-medium text-indigo-600 capitalize">
                        {task?.type || "story"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Priority */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <Flag className="w-5 h-5 text-amber-600" />
                    <h3 className="font-medium text-gray-900">Priority</h3>
                  </div>
                  {isEditing ? (
                    <select
                      value={editedTask.priority || "medium"}
                      onChange={(e) =>
                        setEditedTask({
                          ...editedTask,
                          priority: e.target.value as "low" | "medium" | "high",
                        })
                      }
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="low">🟢 Low</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="high">🔴 High</option>
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">
                        {getPriorityIcon(task?.priority || "medium")}
                      </span>
                      <span className="font-medium text-amber-700 capitalize">
                        {task?.priority || "medium"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <ArrowRight className="w-5 h-5 text-slate-600" />
                    <h3 className="font-medium text-gray-900">Status</h3>
                  </div>
                  {isEditing ? (
                    <select
                      value={editedTask.taskColumn || "todo"}
                      onChange={(e) =>
                        setEditedTask({ ...editedTask, taskColumn: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                    >
                      {/* Load status options dynamically from org settings stored in localStorage as fallback */}
                      {(() => {
                        try {
                          const org = localStorage.getItem("organizationData");
                          if (org) {
                            const parsed = JSON.parse(org);
                            if (Array.isArray(parsed.workflowStatuses) && parsed.workflowStatuses.length > 0) {
                              return parsed.workflowStatuses.map((s: string) => (
                                <option key={s} value={s}>{s}</option>
                              ));
                            }
                          }
                        } catch (e) {
                          // ignore
                        }
                        return (
                          <>
                            <option value="todo">📋 To Do</option>
                            <option value="inProgress">⚡ In Progress</option>
                            <option value="deployed">🚀 Deployed</option>
                            <option value="done">✅ Done</option>
                          </>
                        );
                      })()}
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                          statusColors[
                            task?.taskColumn as keyof typeof statusColors
                          ] || "bg-gray-50 text-gray-700 border-gray-200"
                        }`}
                      >
                        {task?.taskColumn
                          ? columnLabels[
                              task.taskColumn as keyof typeof columnLabels
                            ]
                          : "Unassigned"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Description */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2 text-gray-400" />
                    Description
                  </h3>
                  {isEditing ? (
                    <textarea
                      value={editedTask.description || ""}
                      onChange={(e) =>
                        setEditedTask({
                          ...editedTask,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter description..."
                      className="w-full h-32 bg-white border border-gray-300 rounded-lg p-3 text-gray-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="bg-white rounded-lg p-3 border border-gray-200 min-h-[100px]">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {task?.description || "No description provided."}
                      </p>
                    </div>
                  )}
                </div>

                {/* Creation Info */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-green-600" />
                    Created By
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {task?.createdByUserName || "Unknown User"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {task?.createdAt
                            ? new Date(task.createdAt).toLocaleString()
                            : "Unknown date"}
                        </p>
                        {!task?.createdByUserName && (
                          <p className="text-xs text-orange-500 mt-1">
                            ⚠️ Api issue: createdByUserName missing
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachments Section */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 flex items-center">
                      <Paperclip className="w-5 h-5 mr-2 text-purple-600" />
                      Attachments (
                      {(() => {
                        const newFormatAttachments =
                          editedTask?.attachments || task?.attachments || [];
                        const legacyAttachmentUrls =
                          editedTask?.attachmentUrls ||
                          task?.attachmentUrls ||
                          [];
                        const attachments =
                          newFormatAttachments.length > 0
                            ? newFormatAttachments
                            : legacyAttachmentUrls;
                        return attachments.length;
                      })()}
                      )
                    </h3>
                    {/* Show upload button for existing stories (numeric ID) or when editing */}
                    {(isEditing || task?.id) && (
                      <label
                        className={`inline-flex items-center px-3 py-1.5 text-sm rounded-lg transition-colors cursor-pointer ${
                          !task?.id ||
                          (typeof task.id === "string" &&
                            task.id.startsWith("item-"))
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-purple-600 text-white hover:bg-purple-700"
                        }`}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        {isUploadingFile
                          ? "Uploading..."
                          : !task?.id ||
                            (typeof task.id === "string" &&
                              task.id.startsWith("item-"))
                          ? "Save First"
                          : "Upload"}
                        <input
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          accept="image/*,.pdf,.doc,.docx,.txt"
                          disabled={
                            isUploadingFile ||
                            !task?.id ||
                            (typeof task.id === "string" &&
                              task.id.startsWith("item-"))
                          }
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {(() => {
                    const newFormatAttachments =
                      editedTask?.attachments || task?.attachments || [];
                    const legacyAttachmentUrls =
                      editedTask?.attachmentUrls || task?.attachmentUrls || [];
                    const attachments =
                      newFormatAttachments.length > 0
                        ? newFormatAttachments
                        : legacyAttachmentUrls;

                    return attachments && attachments.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {attachments.map((attachment, index) => {
                          const isNewFormat =
                            typeof attachment === "object" && attachment.url;
                          const url = isNewFormat
                            ? (attachment as any).url
                            : (attachment as string);
                          const filename = isNewFormat
                            ? (attachment as any).originalFilename
                            : getFileName(url);
                          const thumbnailUrl = isNewFormat
                            ? (attachment as any).thumbnailUrl
                            : null;

                          const isImage =
                            /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(
                              filename
                            );
                          const isDeleting = deletingAttachment === url;

                          return (
                            <div
                              key={index}
                              className="relative group bg-white rounded-lg border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-purple-300"
                            >
                              {/* Thumbnail/File Icon */}
                              <button
                                onClick={() =>
                                  handleViewAttachment(
                                    filename,
                                    url,
                                    thumbnailUrl
                                  )
                                }
                                className="w-full h-24 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors focus:outline-none"
                                disabled={isDeleting}
                              >
                                {isImage && thumbnailUrl ? (
                                  <AuthenticatedImage
                                    thumbnailPath={thumbnailUrl}
                                    alt={filename}
                                    className="w-full h-full object-cover"
                                    fallbackIcon={
                                      <div className="flex flex-col items-center justify-center text-gray-400">
                                        <Image className="w-8 h-8 mb-1" />
                                        <span className="text-xs">Preview</span>
                                      </div>
                                    }
                                  />
                                ) : (
                                  <div className="flex flex-col items-center justify-center text-gray-400 p-2">
                                    {getFileIcon(url)}
                                    <span className="text-xs mt-1 text-center line-clamp-1">
                                      {filename.split(".").pop()?.toUpperCase()}
                                    </span>
                                  </div>
                                )}
                              </button>

                              {/* Filename */}
                              <div className="p-2">
                                <p
                                  className="text-xs font-medium text-gray-900 truncate mb-1"
                                  title={filename}
                                >
                                  {filename}
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500">
                                    {isImage ? "Image" : "File"}
                                  </span>
                                  <div className="flex items-center space-x-1">
                                    {/* View Button */}
                                    <button
                                      onClick={() =>
                                        handleViewAttachment(
                                          filename,
                                          url,
                                          thumbnailUrl
                                        )
                                      }
                                      className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                      title="View"
                                    >
                                      <Eye className="w-3 h-3" />
                                    </button>

                                    {/* Download Button */}
                                    <button
                                      onClick={() => downloadFile(url)}
                                      className="p-1 text-green-600 hover:text-green-800 transition-colors"
                                      title="Download"
                                    >
                                      <Download className="w-3 h-3" />
                                    </button>

                                    {/* Delete Button */}
                                    <button
                                      onClick={() =>
                                        handleRemoveAttachment(filename, url)
                                      }
                                      disabled={isDeleting}
                                      className="p-1 text-red-600 hover:text-red-800 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                                      title="Remove"
                                    >
                                      {isDeleting ? (
                                        <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                      ) : (
                                        <Trash2 className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Loading Overlay */}
                              {isDeleting && (
                                <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center">
                                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-white p-6 rounded-lg border border-purple-200 text-center">
                        <Paperclip className="w-12 h-12 mx-auto mb-3 text-purple-300" />
                        <p className="text-sm text-gray-600 mb-2">
                          No attachments yet
                        </p>
                        <p className="text-xs text-gray-400">
                          Upload images, documents, or PDFs to get started
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Comments & Activity Section */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                  Comments & Activity
                </h3>
                {/* Tab Navigation */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab("comments")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      activeTab === "comments"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Comments ({comments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab("activity")}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      activeTab === "activity"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <Activity className="w-4 h-4 inline mr-1" />
                    Activity ({workLogs.length})
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === "comments" && (
                <>
                  {/* Add Comment */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          className="w-full h-20 bg-white border border-gray-300 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || isAddingComment}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {isAddingComment ? "Adding..." : "Comment"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-4">
                    {isLoadingComments ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-sm text-gray-500 mt-3">
                          Loading comments...
                        </p>
                      </div>
                    ) : (
                      <>
                        {comments.map((comment) => (
                          <div
                            key={comment.id}
                            className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-100"
                          >
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {comment.author.username}
                                </span>
                                <span className="text-sm text-gray-500">
                                  commented
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border whitespace-pre-wrap">
                                {comment.text}
                              </p>
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(comment.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}

                        {comments.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">
                              No comments yet. Be the first to comment!
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Activity Tab */}
              {activeTab === "activity" && (
                <div className="space-y-4">
                  {isLoadingWorkLogs ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-3">
                        Loading activity...
                      </p>
                    </div>
                  ) : (
                    <>
                      {workLogs.map((workLog) => {
                        // Debug log to see what data we have
                        console.log("🔍 Work log data:", workLog);

                        return (
                          <div
                            key={workLog.id}
                            className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-100"
                          >
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <Activity className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                {workLog.actorFirstName &&
                                workLog.actorLastName ? (
                                  <>
                                    <span className="font-medium text-gray-900">
                                      {workLog.actorFirstName}{" "}
                                      {workLog.actorLastName}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      performed action
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="font-medium text-gray-900">
                                      System
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      logged activity
                                    </span>
                                  </>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg border">
                                {workLog.description}
                              </p>
                              <div className="flex items-center space-x-4 mt-2">
                                <p className="text-xs text-gray-400">
                                  {new Date(workLog.createdAt).toLocaleString()}
                                </p>
                                {workLog.hours && (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                    {workLog.hours}h logged
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {workLogs.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p className="text-sm">
                            No activity logs yet. Changes to this story will
                            appear here.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modern Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-500">
            Last updated:{" "}
            {task?.createdAt
              ? new Date(task.createdAt).toLocaleDateString()
              : "Unknown"}
          </div>

          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Story
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Attachment Popup Modal */}
      {showAttachmentPopup && currentAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header - Sticky and always visible */}
            <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 bg-white z-10 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate flex-1 pr-4">
                {currentAttachment.filename}
              </h3>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={() => downloadFile(currentAttachment.url)}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  title="Download"
                >
                  <Download className="w-4 h-4 mr-1" />
                  <span>Download</span>
                </button>
                <button
                  onClick={handleDeleteCurrentAttachment}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span>Delete</span>
                </button>
                <button
                  onClick={closeAttachmentPopup}
                  className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - Dynamic height based on browser */}
            <div
              className="flex-1 overflow-auto p-4"
              style={{ maxHeight: "calc(90vh - 80px)" }}
            >
              {currentAttachment.thumbnailUrl ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-full h-full flex items-center justify-center">
                    <AuthenticatedImage
                      thumbnailPath={currentAttachment.url}
                      alt={currentAttachment.filename}
                      className="max-w-full max-h-full object-contain rounded"
                      fallbackIcon={
                        <div className="bg-gray-100 p-8 rounded flex flex-col items-center">
                          <FileText className="w-16 h-16 text-gray-400 mb-4" />
                          <p className="text-gray-600 text-center">
                            Preview not available
                          </p>
                        </div>
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4 text-sm">
                      Preview not available for this file type
                    </p>
                    <p className="text-gray-400 text-xs">
                      Use the download button above to save the file
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    // Inline rendering (no fullscreen overlay); container handles its own spacing
    return <div className="w-full p-0">{content}</div>;
  }

  // Default modal overlay rendering
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto"
      style={{ zIndex: 9999 }}
    >
      {content}
    </div>
  );
};

export default StoryModal;
